import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, cant, buscar } from '../utils/formato';

// Qué hay en bodega.
//
// Los agotados y los que quedan bajos salen PRIMERO, no ordenados por nombre.
// Una lista alfabética obliga a recorrerla entera para encontrar lo que hay que
// reponer, y entonces nadie la mira.

const PINTA = {
  agotado: ['malo',  '🔴 Agotado'],
  bajo:    ['ojo',   '🟡 Queda poco'],
  bien:    ['bueno', '🟢 Bien'],
};

function Inventario({ irA, onCambio }) {
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [soloAlertas, setSoloAlertas] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [ajustando, setAjustando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [abriendoAvisos, setAbriendoAvisos] = useState(false);
  const [avisos, setAvisos] = useState({ telegram_chat_id: '', hay_token: false });

  const cargar = useCallback(() => {
    setCargando(true);
    axios.get('/api/inventario')
      .then(r => setProductos(r.data || []))
      .catch(() => {})
      .finally(() => setCargando(false));
    axios.get('/api/avisos')
      .then(r => setAvisos(r.data || { telegram_chat_id: '', hay_token: false }))
      .catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const guardarAvisos = async () => {
    setGuardando(true);
    try {
      await axios.put('/api/avisos', { telegram_chat_id: avisos.telegram_chat_id });
      setAviso({ tipo: 'bueno', texto: avisos.telegram_chat_id
        ? 'Listo. Los avisos de bodega van a llegar a ese chat.'
        : 'Avisos apagados. La pantalla sigue mostrando las alertas.' });
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const probarAviso = async () => {
    setGuardando(true);
    try {
      await axios.post('/api/avisos/probar', { telegram_chat_id: avisos.telegram_chat_id });
      setAviso({ tipo: 'bueno', texto: 'Mensaje enviado. Mire su Telegram.' });
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo enviar' });
    }
    setGuardando(false);
  };

  const revisarAhora = async () => {
    setGuardando(true);
    try {
      const { data } = await axios.post('/api/avisos/revisar');
      setAviso({ tipo: 'bueno', texto: data.cuantos === 0
        ? 'Revisado: nada empeoró desde la última vez, así que no se mandó nada.'
        : `Revisado: ${data.cuantos} ${data.cuantos === 1 ? 'material' : 'materiales'} ` +
          (data.enviado ? 'y el aviso salió.' : 'pero el aviso no pudo salir.') });
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo revisar' });
    }
    setGuardando(false);
  };

  const conAlerta = productos.filter(p => p.alerta !== 'bien');
  const base = soloAlertas ? conAlerta : productos;
  const visibles = buscar(base, busqueda);

  const valorCosto = productos.reduce((s, p) => s + Math.max(0, p.valor_costo || 0), 0);
  const valorVenta = productos.reduce((s, p) => s + Math.max(0, p.valor_venta || 0), 0);

  const ajustar = async (e) => {
    e.preventDefault();
    const u = parseFloat(ajustando.unidades);
    if (!u) return alert('El ajuste no puede ser cero');
    setGuardando(true);
    try {
      await axios.post(`/api/inventario/${ajustando.producto.id}/ajustar`, {
        unidades: u,
        nota: ajustando.nota || null,
      });
      setAviso({
        tipo: 'bueno',
        texto: `${ajustando.producto.nombre}: ${u > 0 ? 'se sumaron' : 'se restaron'} ` +
               `${cant(Math.abs(u), ajustando.producto.unidad)}`,
      });
      setAjustando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo ajustar');
    }
    setGuardando(false);
  };

  return (
    <>
      <div className="encabezado">
        <h1>Inventario</h1>
        <p>Qué hay, cuánto vale y qué toca reponer</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      {conAlerta.length > 0 && !soloAlertas && (
        <div className="aviso ojo">
          <strong>
            {conAlerta.length === 1
              ? 'Hay 1 producto que necesita atención'
              : `Hay ${conAlerta.length} productos que necesitan atención`}
          </strong>
          {' — '}
          {conAlerta.filter(p => p.alerta === 'agotado').length} agotados y{' '}
          {conAlerta.filter(p => p.alerta === 'bajo').length} por debajo del mínimo.
          <button className="boton chico" style={{ marginLeft: 10 }}
                  onClick={() => setSoloAlertas(true)}>
            Ver solo esos
          </button>
        </div>
      )}

      {/* A dónde llega el aviso.
          Va en Inventario y no en una pantalla de configuración aparte porque
          es aquí donde uno se da cuenta de que quiere que le avisen: mirando
          la lista de lo que se acabó. */}
      <div className="tarjeta" style={{ marginBottom: 14 }}>
        <button className="boton chico suave"
                onClick={() => setAbriendoAvisos(!abriendoAvisos)}>
          📢 Avisarme al teléfono cuando algo se esté acabando
          {' '}{abriendoAvisos ? '▲' : '▼'}
        </button>

        {abriendoAvisos && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!avisos.hay_token && (
              <div className="aviso ojo">
                El servidor todavía no tiene la llave del bot de Telegram, así que
                los avisos no van a salir aunque guarde el chat aquí.
              </div>
            )}
            <p style={{ margin: 0, fontSize: 14, color: 'var(--tinta-tenue)' }}>
              Escríbale a su bot de Telegram, abra{' '}
              <code>api.telegram.org/bot&lt;llave&gt;/getUpdates</code> y copie el
              número que sale en <code>chat.id</code>.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <input value={avisos.telegram_chat_id} placeholder="123456789"
                     style={{ maxWidth: 220 }}
                     onChange={e => setAvisos({ ...avisos, telegram_chat_id: e.target.value })} />
              <button className="boton chico" disabled={guardando} onClick={guardarAvisos}>
                💾 Guardar
              </button>
              {/* Probar no es un lujo: un aviso mal configurado no falla,
                  CALLA — y un aviso que calla se descubre el día que hacía
                  falta. */}
              <button className="boton chico suave" disabled={guardando} onClick={probarAviso}>
                Mandarme una prueba
              </button>
              <button className="boton chico suave" disabled={guardando} onClick={revisarAhora}>
                Revisar bodega ahora
              </button>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
              Se revisa a las 7, 10, 1, 4 y 7. Solo llega mensaje cuando algo
              <strong> empeora</strong> — si un material lleva días bajo no se
              repite, para que los avisos se sigan leyendo.
            </p>
          </div>
        )}
      </div>

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--acero)' }}>
          <div className="rotulo">📦 Valor en bodega</div>
          <div className="valor cifra">{plata(valorCosto)}</div>
          <div className="pie">a lo que costó</div>
        </div>
        <div className="dato" style={{ '--color': 'var(--venta)' }}>
          <div className="rotulo">🏷️ Si se vendiera todo</div>
          <div className="valor cifra">{plata(valorVenta)}</div>
          <div className="pie">
            dejaría {plata(valorVenta - valorCosto)}
          </div>
        </div>
        <div className="dato" style={{ '--color': conAlerta.length ? 'var(--ojo)' : 'var(--bueno)' }}>
          <div className="rotulo">⚠️ Para reponer</div>
          <div className="valor cifra">{conAlerta.length}</div>
          <div className="pie">
            {conAlerta.length === 0 ? 'todo en orden' : 'agotados o bajos'}
          </div>
        </div>
      </div>

      <div className="bloque">
        <h2>
          📦 Productos
          <span className="contador">
            {visibles.length}
            {visibles.length !== productos.length && ` de ${productos.length}`}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {conAlerta.length > 0 && (
              <button className={'boton chico' + (soloAlertas ? '' : ' suave')}
                      onClick={() => setSoloAlertas(!soloAlertas)}>
                {soloAlertas ? 'Ver todos' : 'Solo alertas'}
              </button>
            )}
            <button className="boton chico suave" onClick={() => irA('productos')}>
              ➕ Nuevo producto
            </button>
          </span>
        </h2>

        <div className="cuerpo" style={{ paddingBottom: 0 }}>
          <div className="buscador">
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                   placeholder="🔎 Buscar por código, nombre o referencia" />
            {busqueda && (
              <button type="button" onClick={() => setBusqueda('')}>×</button>
            )}
          </div>
        </div>

        {cargando ? (
          <div className="vacio">Cargando…</div>
        ) : visibles.length === 0 ? (
          <div className="vacio">
            {busqueda ? `Ningún producto coincide con «${busqueda}»`
                      : 'Todavía no hay productos.'}
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Producto</th>
                  <th style={{ textAlign: 'right' }}>Hay</th>
                  <th style={{ textAlign: 'right' }}>Mínimo</th>
                  <th style={{ textAlign: 'right' }}>Costó</th>
                  <th style={{ textAlign: 'right' }}>Vende a</th>
                  <th style={{ textAlign: 'right' }}>Deja</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibles.map(p => {
                  const [clase, texto] = PINTA[p.alerta] || PINTA.bien;
                  return (
                    <tr key={p.id}>
                      <td className="codigo">{p.codigo}</td>
                      <td className="recio">
                        {p.nombre}
                        {(p.referencia || p.categoria) && (
                          <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', fontWeight: 400 }}>
                            {[p.referencia, p.categoria].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="num recio"
                          style={{ color: p.alerta === 'agotado' ? 'var(--malo)'
                                 : p.alerta === 'bajo' ? 'var(--ojo)' : undefined }}>
                        {cant(p.existencia, p.unidad)}
                      </td>
                      <td className="num" style={{ color: 'var(--tinta-tenue)' }}>
                        {p.stock_minimo > 0 ? cant(p.stock_minimo) : '—'}
                      </td>
                      <td className="num">{p.costo_unitario > 0 ? plata(p.costo_unitario) : '—'}</td>
                      <td className="num">{plata(p.precio_venta)}</td>
                      <td className="num"
                          style={{ color: p.ganancia_unidad < 0 ? 'var(--malo)' : 'var(--bueno)' }}>
                        {p.costo_unitario > 0 ? plata(p.ganancia_unidad) : '—'}
                        {p.margen_pct != null && (
                          <div style={{ fontSize: 11 }}>{p.margen_pct.toFixed(0)}%</div>
                        )}
                      </td>
                      <td><span className={'pinta ' + clase}>{texto}</span></td>
                      <td>
                        <button className="boton chico suave"
                                onClick={() => setAjustando({ producto: p, unidades: '', nota: '' })}>
                          Ajustar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ajuste manual */}
      {ajustando && (
        <div className="bloque">
          <h2>⚖️ Ajustar {ajustando.producto.nombre}</h2>
          <form className="cuerpo" onSubmit={ajustar}>
            <div className="aviso">
              Hay <strong>{cant(ajustando.producto.existencia, ajustando.producto.unidad)}</strong>{' '}
              según el sistema. Un ajuste queda registrado como un movimiento más, con su
              motivo — no se reescribe el número en silencio.
            </div>
            <div className="campos">
              <label className="campo">
                <span>Cuántas sumar o restar *</span>
                <input type="number" step="0.01" className="num-grande" required autoFocus
                       value={ajustando.unidades} placeholder="Ej: -3 si se rompieron 3"
                       onChange={e => setAjustando({ ...ajustando, unidades: e.target.value })} />
              </label>
              <label className="campo">
                <span>Por qué</span>
                <input value={ajustando.nota} placeholder="Conteo, rotura, pérdida…"
                       onChange={e => setAjustando({ ...ajustando, nota: e.target.value })} />
              </label>
            </div>
            {parseFloat(ajustando.unidades) ? (
              <div className="aviso" style={{ marginTop: 14, marginBottom: 0 }}>
                Quedarían{' '}
                <strong>
                  {cant(ajustando.producto.existencia + parseFloat(ajustando.unidades),
                        ajustando.producto.unidad)}
                </strong>
              </div>
            ) : null}
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setAjustando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Ajustar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Inventario;
